import EEPlaceholder from 'components/Sections/EEPlaceholder';
import { ComponentWithContextProps } from 'lib/component-props';
import { useI18n } from 'next-localization';
import useHelpers from 'hooks/helpers';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getShareclasses,
  getAvailableTabs,
  getFormJSON,
  getIsFundDetailsLoading,
} from '@/store/fundDetails';
import EloquaForm from 'components/Sections/EloquaForm/EloquaForm';
import FundDetailsFundsFactsTab from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsFundsFactsTab';
import FundDetailsThaiPerformanceTab from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsThaiPerformanceTab';
import FundDetailsPerformanceTab from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsPerformanceTab';
import FundDetailsManagementTab from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsManagementTab';
import FundDetailsLiteratureTab from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsLiteratureTab';
import FundDetailsHoldingsTab from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsHoldingsTab';
import FundDetailsBreakdownTab from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsBreakdownTab/FundDetailsBreakdownTab';
import FundDetailsPremiumDiscountTab from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsPremiumDiscountTab/FundDetailsPremiumDiscountTab';
import FundDetailsPodcastTab from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsPodcastTab';
import FirstLevelTabs from 'components/Generic/FirstLevelTabs';
import BaseSkeleton from 'components/Generic/BaseSkeleton';

import { AvailableTabs } from '@/store/fundDetails/typings';

const FundDetailsTabs = ({ sitecoreContext }: ComponentWithContextProps): JSX.Element => {
  const { updateUrlParams } = useHelpers();
  const [activeTabKey, setActiveTabKey] = useState(updateUrlParams().tab);
  const { t } = useI18n();

  const isLoading = useSelector(getIsFundDetailsLoading);
  const availableTabs: AvailableTabs = useSelector(getAvailableTabs);
  const formJson = useSelector(getFormJSON);
  const shareClasses = useSelector(getShareclasses);

  const tabTitlesByTabKeys = useMemo(() => {
    return {
      fundFactsTab: t('fundDetailsTabsFundFacts'),
      performanceTab: t('fundDetailsTabsPerformance'),
      managementTab: t('fundDetailsTabsManagement'),
      literatureTab: t('fundDetailsTabsLiterature'),
      holdingsTab: t('fundDetailsTabsHoldings'),
      breakdownTab: t('fundDetailsTabsBreakdown'),
      premiumDiscountTab: t('fundDetailsTabsPremiumDiscount'),
      podcastTab: t('fundDetailsTabsQuarterlyUpdatePodcast'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs: Array<{ key: string; title: string }> = useMemo(() => {
    if (!availableTabs) return [];
    let tabsToShow: any = Object.entries(availableTabs);

    tabsToShow = tabsToShow.filter(([key, value]: [string, string | any]) => {
      if (!tabTitlesByTabKeys[key] || value.tab === false) return false;

      if (typeof value === 'object') {
        return Object.values(value).some((value: string) => {
          if (typeof value === 'object') {
            return Object.values(value).some((value) => value);
          }
          return value;
        });
      }
      return value;
    });
    tabsToShow = tabsToShow.map(([key]: string) => ({ key, title: tabTitlesByTabKeys[key] }));
    return tabsToShow;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTabs, tabTitlesByTabKeys]);

  const showThaiPerformanceTab = useMemo(() => {
    return (
      availableTabs?.performanceTab?.thaiCumulativeTable ||
      availableTabs?.performanceTab?.thaiCumulativeDailyTable ||
      availableTabs?.performanceTab?.thaiAnnualizedTable ||
      availableTabs?.performanceTab?.thaiAnnualizedDailyTable
    );
  }, [availableTabs]);

  const tabComponentsByTabKeys = useMemo(() => {
    return {
      fundFactsTab: FundDetailsFundsFactsTab,
      performanceTab: showThaiPerformanceTab
        ? FundDetailsThaiPerformanceTab
        : FundDetailsPerformanceTab,
      managementTab: FundDetailsManagementTab,
      literatureTab: FundDetailsLiteratureTab,
      holdingsTab: FundDetailsHoldingsTab,
      breakdownTab: FundDetailsBreakdownTab,
      premiumDiscountTab: FundDetailsPremiumDiscountTab,
      podcastTab: FundDetailsPodcastTab,
    };
  }, [showThaiPerformanceTab]);

  const ActiveTabComponent = useMemo(() => {
    return tabComponentsByTabKeys[activeTabKey];
  }, [activeTabKey, tabComponentsByTabKeys]);

  const shareClassesLength = useMemo(() => {
    return shareClasses?.length !== 0;
  }, [shareClasses]);

  const eloquaFormFields = useMemo(() => {
    return formJson ? { formJson: { value: JSON.parse(formJson) } } : undefined;
  }, [formJson]);

  const performanceTabIncludesSubTabs = useMemo(() => {
    if (!availableTabs?.performanceTab) return [];

    const subTabs = [];

    for (const [key, value] of Object.entries(availableTabs.performanceTab)) {
      if (key !== 'tab' && key !== 'graphSection' && value) {
        subTabs.push(key);
      }
    }

    return subTabs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTabs, tabComponentsByTabKeys]);

  const handleTabClick = (tabKey: string) => {
    setActiveTabKey(tabKey);
    if (tabKey === 'performanceTab') {
      updateUrlParams([{ name: 'subTab2', value: '' }]);
      if (performanceTabIncludesSubTabs.length === 0) {
        updateUrlParams([{ name: 'subTab', value: '' }]);
      }
    } else {
      updateUrlParams([{ name: 'subTab', value: '' }]);
      updateUrlParams([{ name: 'subTab2', value: '' }]);
    }
  };

  useEffect(() => {
    updateUrlParams([{ name: 'tab', value: activeTabKey }]);

    return () => {
      updateUrlParams([{ name: 'subTab', value: '' }]);
      updateUrlParams([{ name: 'subTab2', value: '' }]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabKey, availableTabs]);

  useEffect(() => {
    if (!tabs.length) return;
    const tabExists = tabs?.some((tab) => tab.key === activeTabKey);
    if (!tabExists) setActiveTabKey(tabs[0]?.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, activeTabKey]);

  if (sitecoreContext.pageEditing) {
    return <EEPlaceholder>Fund Details Tabs</EEPlaceholder>;
  } else if (isLoading) {
    return <BaseSkeleton height="64px" />;
  }

  return (
    <>
      {tabs.length !== 0 && shareClassesLength && (
        <>
          <FirstLevelTabs tabs={tabs} activeTabKey={activeTabKey} tabClick={handleTabClick} />
          {ActiveTabComponent ? (
            <ActiveTabComponent title={tabTitlesByTabKeys[activeTabKey]} />
          ) : null}

          {eloquaFormFields && <EloquaForm fields={eloquaFormFields} />}
        </>
      )}
    </>
  );
};

export default FundDetailsTabs;
